import { observable, runInAction, computed, action } from 'mobx';
import {
    getPopulationList,
    getChargedAudience,
    pushPopulation,
    searchInetCelebrity,
    bindCheckForOrientation,
    pushPopulationAgain,
} from 'service/population';
import { getFansStarLabels, getAppInterestLabels, getIndustryIntension } from 'service/configs';

import { InetCelebrity } from './interface';
import { PopulationStatus } from './config';
import autobind from 'autobind-decorator';
import { PopulationType } from 'constants/constants';
import { addRadarEvent } from 'utils/businesslog';
import { message } from 'm-ui';

export interface Population {
    orientationId: number;
    orientationName: string;
    populationType: number;
    status: number;
    coverNum: number;
    recordSize: number;
    matchSize: number;
    groupName: string | null;
    createTime: number;
    profileTags: string | null;
    extendTo: number;
    srcId: number;

    accountId: number;
    modifyTimes: number;
    portraitStatus: number;
    pushedTime: number;
    sourceType: number;
    type: number;
    verifyTime: number;
    profile: boolean;
    dailyCoverNum: number;
    displayAlliance?: boolean;
    allianceCoverNum?: number;
    realTime?: number;
    matchByGid?: boolean; // 上传人群中类型匹配是否为设备号匹配
}

export interface PaidAudience {
    id: number;
    name: string;
    tpName: string;
    tpCode: string;
    uid: string;
    updateTime: number;
    createTime: number;
}

export interface AppNameOptions {
    isChecked: boolean;
    label: string;
}

export class PopulationModel {
    @observable
    populationListAll: Population[] = [];

    @observable
    tableLoading: boolean = false;

    @observable
    chargedAudience: PaidAudience[] = [];

    @observable
    fansStarOptions: any = [];

    @observable
    shopIndustry: any = [];

    @observable
    iNetCelebrity: InetCelebrity[] = [];

    @observable
    appInterestsOptions: string[] = [];

    @observable
    appNameOptions: AppNameOptions[] = [];

    // 具有绑定状态的集合
    bindPopulationStatus: Set<number> = new Set();

    @computed
    get takingEffectPopulationList() {
        return this.populationListAll.filter((p) =>
            [PopulationStatus.VALID, PopulationStatus.PUSHING, PopulationStatus.PUSHED].includes(
                p.status,
            ),
        );
    }

    @computed
    get canExtendPopulationList() {
        return this.populationListAll.filter((p) => PopulationModel.canExtend(p));
    }

    /** 获取有绑定列表的状态 */
    @autobind
    async getListBindStatus(orientationIds: number[]) {
        const { bind, unbind } = await bindCheckForOrientation({ orientationIds });
        bind.forEach((bind) => {
            this.bindPopulationStatus.add(bind);
        });
        unbind.forEach((unbind) => {
            this.bindPopulationStatus.delete(unbind);
        });
    }

    /**
     * 判读人群包是否有绑定状态
     * @param populationId
     */
    @autobind
    checkHasBindStatus(populationId: number) {
        return this.bindPopulationStatus.has(populationId);
    }

    @action
    @autobind
    async getPopulationListAll() {
        if (this.tableLoading) {
            return this.populationListAll;
        }
        runInAction(() => {
            this.tableLoading = true;
        });
        try {
            const { data } = await getPopulationList({
                populationType: -1,
                sortingColumn: '',
                order: 0,
                orientationName: '',
                status: -1,
            });
            runInAction(() => {
                this.populationListAll = data;
            });
            if (data) {
                addRadarEvent({
                    name: 'SLI_DMP_POP_LIST_LOAD',
                    result_type: 'success',
                    message: '人群包列表加载成功',
                });
            } else {
                addRadarEvent({
                    name: 'SLI_DMP_POP_LIST_LOAD',
                    result_type: 'fail',
                    message: '人群包列表加载失败',
                });
            }
        } catch (error) {
            addRadarEvent({
                name: 'SLI_DMP_POP_LIST_LOAD',
                result_type: 'fail',
                message: '人群包列表加载失败',
                extra_info: JSON.stringify(error),
            });
        } finally {
            runInAction(() => {
                this.tableLoading = false;
            });
        }
        return this.populationListAll;
    }

    async getINetCelebrity(query) {
        this.iNetCelebrity = [];
        if (query === '') {
            return;
        }
        const labels = await searchInetCelebrity({ query });
        runInAction(() => {
            this.iNetCelebrity = labels;
        });
    }

    async getChargedAudience() {
        const { paidAudience } = await getChargedAudience();
        runInAction(() => (this.chargedAudience = paidAudience));
    }

    async getLabels() {
        const [fansStarOptions, appInterstOptions, shopIndustry] = await Promise.all([
            getFansStarLabels(),
            getAppInterestLabels(),
            getIndustryIntension(),
        ]);

        runInAction(() => {
            this.fansStarOptions = fansStarOptions;
            this.appInterestsOptions = appInterstOptions;
            this.shopIndustry = shopIndustry;
        });
    }

    static async push(p: Population) {
        try {
            const res = await pushPopulation(p.orientationId);
            if (res?.result === 1) {
                message.success('已操作，人群包上线后即可投放或跨账户推送', 6);
                addRadarEvent({
                    name: 'SLI_DMP_POP_PUBLISH',
                    result_type: 'success',
                    message: '人群包上线成功',
                });
            } else {
                addRadarEvent({
                    name: 'SLI_DMP_POP_PUBLISH',
                    result_type: 'fail',
                    message: '人群包上线失败',
                });
            }
        } catch (error) {
            addRadarEvent({
                name: 'SLI_DMP_POP_PUBLISH',
                result_type: 'fail',
                message: '人群包上线失败',
                extra_info: JSON.stringify(error),
            });
        }
    }

    static async pushAgain(p: Population) {
        await pushPopulationAgain(p.orientationId);
    }

    // static functions

    static canExtend(p: Population) {
        // 上传类型的人群包 5w ~ 2kw
        // 其它人群包 50w ~ 2kw
        // 三方人群包不支持扩展
        // 上传人群类型匹配选择「设备号匹配」不支持扩展
        return (
            ![PopulationType.PAID].includes(p.populationType) &&
            [
                PopulationStatus.VALID,
                PopulationStatus.PUSHING,
                PopulationStatus.PUSHED,
                PopulationStatus.PUSH_FAIL,
            ].includes(p.status) &&
            (([PopulationType.UPLOAD].includes(p.populationType) &&
                p.dailyCoverNum >= 3000 &&
                p.dailyCoverNum <= 20000000) ||
                (![PopulationType.UPLOAD].includes(p.populationType) &&
                    p.dailyCoverNum >= 3000 &&
                    p.dailyCoverNum <= 20000000)) &&
            !p.matchByGid
        );
    }

    static canPush(p: Population) {
        return p.status === PopulationStatus.VALID || p.status === PopulationStatus.PUSH_FAIL;
    }

    static canPushAgain(p: Population) {
        return p.status === PopulationStatus.OVERDUE && p.populationType !== PopulationType.EXTEND;
    }

    static canPushAccount(p: Population) {
        return (
            p.status === PopulationStatus.PUSHED &&
            p.populationType !== PopulationType.S_RECOMMEND &&
            p.populationType !== PopulationType.MZ_GENDER_AGE
        );
    }

    static canDiagram(p: Population) {
        return (
            (p.status === PopulationStatus.PUSHED || p.status === PopulationStatus.SOON_INVALID) &&
            ![PopulationType.PAID, PopulationType.LIVE_PAID].includes(p.populationType)
        );
    }

    static pushing(p: Population) {
        return p.status === PopulationStatus.PUSHING;
    }

    static cannotPush(p: Population) {
        return (
            p.status === PopulationStatus.INVALID ||
            p.status === PopulationStatus.COMPUTE_FAIL ||
            p.status === PopulationStatus.OVERDUE
        );
    }

    static pushed(p: Population) {
        return p.status === PopulationStatus.PUSHED;
    }
}

const populationModel = new PopulationModel();

export default populationModel;
